import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	FlatList,
	RefreshControl,
	View,
} from 'react-native';
import { useMutation } from '@apollo/client';
import Toast from 'react-native-root-toast';

import { ROLE_PERMISSIONS, STATUS } from '../../../common/constants';
import { valueExists } from '../../../common/utils/utils';
import { formatUncountedProductsPhrase } from '../../../common/utils/warehousing/formatUncountedProductsPhrase';
import { useUserContext } from '../../../common/utils/context';

import { MODIFY_STOCKTAKE_ITEM, ZERO_UNCOUNTED_STOCKTAKE_ITEMS } from '../../../common/graphql/stocktakeItem';
import { MODIFY_WAREHOUSE_PRODUCT_QUANTITY } from '../../../common/graphql/warehouseProduct';

import { DetailContainer } from '../../containers';
import { useRefreshPage } from '../../hooks/useRefreshPage';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import useReportDockAction from '../../hooks/useReportDockAction';
import { Spinner } from '../../components';
import {
	EmptyState,
	SecondaryButton,
} from '../../design/components';
import { Alert } from '../../components/other/Alert';

import {
	findStocktakeItemByBarcode,
	normalizeBarcode,
	patchStocktakeItemInList,
} from './StocktakeDetailHelpers';
import { createStocktakeDetailStyles } from './StocktakeDetail.styles';
import useStocktakeBatchDialog from './useStocktakeBatchDialog';
import StocktakeDetailHeader from './StocktakeDetailHeader';
import StocktakeItemCard from './StocktakeItemCard';
import StocktakeBatchDialog from './StocktakeBatchDialog';
import StocktakeScannerDialog from './StocktakeScannerDialog';
import ManualProductSelectorDialog from './ManualProductSelectorDialog';

const StocktakeDetail = ({ data: stocktake, refetch, onDockChange }) => {
	const styles = useThemedStyles(createStocktakeDetailStyles);
	const { hasPermission, tenant } = useUserContext();

	const [searchQuery, setSearchQuery] = useState('');
	const [showScannedOnly, setShowScannedOnly] = useState(true);
	const [isScannerOpen, setIsScannerOpen] = useState(false);
	const [isManualSelectOpen, setIsManualSelectOpen] = useState(false);
	const [pendingManualSelectionId, setPendingManualSelectionId] = useState(null);
	const [pendingScannedItem, setPendingScannedItem] = useState(null);
	const [items, setItems] = useState(() => stocktake?.items || []);

	const [modifyStocktakeItemMutation, { loading: updatingItem }] = useMutation(MODIFY_STOCKTAKE_ITEM);
	const [applyStocktakeChangesMutation, { loading: submittingStocktake }] = useMutation(MODIFY_WAREHOUSE_PRODUCT_QUANTITY);
	const [zeroUncountedItemsMutation, { loading: zeroingUncountedItems }] = useMutation(ZERO_UNCOUNTED_STOCKTAKE_ITEMS);

	const [refreshPage, isRefreshing] = useRefreshPage(refetch);
	const isStocktakeCompleted = stocktake?.status === STATUS.COMPLETE;
	const canEditStocktakes = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_STOCKTAKES) && !isStocktakeCompleted;

	const handleSearchChange = useCallback((text) => {
		setSearchQuery(text);
	}, []);

	useEffect(() => {
		setItems(stocktake?.items || []);
	}, [stocktake?.items]);

	const filteredItems = useMemo(() => {
		const query = normalizeBarcode(searchQuery).toLowerCase();

		return items.filter(item => {
			const itemQuantity = valueExists(item.quantity) ? item.quantity : null;

			if (showScannedOnly && !valueExists(itemQuantity)) {
				return false;
			}

			if (!query) {
				return true;
			}

			const nameMatches = item.warehouseProduct?.product?.name?.toLowerCase().includes(query);
			const barcodeMatches = (item.warehouseProduct?.product?.barcodes || [])
				.some(code => normalizeBarcode(code).toLowerCase().includes(query));

			return nameMatches || barcodeMatches;
		});
	}, [items, searchQuery, showScannedOnly]);

	const {
		activeBatchItem,
		batchDialogRows,
		setBatchDialogRows,
		batchDialogWarehouseRows,
		batchDialogError,
		isFetchingWarehouseBatches,
		openBatchDialog,
		closeBatchDialog,
		handleSaveBatches,
		resolvedActiveItem,
	} = useStocktakeBatchDialog({ items, setItems, tenant, modifyStocktakeItemMutation });

	const handleScanResult = useCallback((barcode) => {
		const matchedItem = findStocktakeItemByBarcode(items, barcode);
		if (matchedItem) {
			setPendingScannedItem({
				id: matchedItem.id,
				barcode,
			});
			setIsScannerOpen(false);
		}
		return Boolean(matchedItem);
	}, [items]);

	const handleManualProductSelect = useCallback((selectedItemId) => {
		if (!selectedItemId) {
			return;
		}

		const matchedItem = items.find(item => item.id === selectedItemId);

		if (matchedItem) {
			openBatchDialog(matchedItem, null);
		}
		else {
			Toast.show('Product is not part of this stocktake');
		}
	}, [items, openBatchDialog]);

	const handleManualSelectionRequest = useCallback((itemId) => {
		if (!itemId) {
			return;
		}
		setIsManualSelectOpen(false);
		setPendingManualSelectionId(itemId);
	}, []);

	useEffect(() => {
		if (!isManualSelectOpen && pendingManualSelectionId) {
			const timeoutId = setTimeout(() => {
				handleManualProductSelect(pendingManualSelectionId);
				setPendingManualSelectionId(null);
			}, 250);

			return () => clearTimeout(timeoutId);
		}
	}, [handleManualProductSelect, isManualSelectOpen, pendingManualSelectionId]);

	useEffect(() => {
		if (!isScannerOpen && pendingScannedItem?.id) {
			const timeoutId = setTimeout(() => {
				const matched = items.find(item => item.id === pendingScannedItem.id);
				if (matched) {
					openBatchDialog(matched, pendingScannedItem.barcode);
				}
				else {
					Toast.show('Product not found in this stocktake');
				}
				setPendingScannedItem(null);
			}, 250);

			return () => clearTimeout(timeoutId);
		}
	}, [isScannerOpen, items, openBatchDialog, pendingScannedItem]);

	const handleZeroStocktakeItem = useCallback(async (targetItem) => {
		const targetItemId = targetItem.id || targetItem._id;
		if (!targetItemId) {
			return;
		}

		const previousQuantity = valueExists(targetItem.quantity) ? targetItem.quantity : null;
		const previousCountedExpiryBatches = targetItem.countedExpiryBatches;

		setItems(prevItems => patchStocktakeItemInList(prevItems, targetItemId, {
			quantity: 0,
			countedExpiryBatches: [],
		}));

		// Only the mutation is guarded: a failure past this point (e.g. the toast)
		// must not roll the card back after the count was already saved.
		try {
			await modifyStocktakeItemMutation({
				variables: {
					input: {
						id: targetItemId,
						quantity: 0,
						countedExpiryBatches: [],
					},
				},
			});
		}
		catch (error) {
			setItems(prevItems => patchStocktakeItemInList(prevItems, targetItemId, {
				quantity: previousQuantity,
				countedExpiryBatches: previousCountedExpiryBatches,
			}));
			Toast.show('Unable to zero item');
			return;
		}

		Toast.show('Count set to zero');
	}, [modifyStocktakeItemMutation]);

	const confirmZeroStocktakeItem = useCallback((item) => {
		// Confirm only when overwriting an existing non-zero count; uncounted items zero immediately
		if (valueExists(item.quantity) && item.quantity !== 0) {
			Alert.alert(
				'Set count to zero?',
				// No raw number in the copy — the card displays the count in the tenant's cases/units format
				`${item.warehouseProduct?.product?.name || 'This product'} already has a counted quantity. Overwrite it with 0?`,
				[
					{
						text: 'Cancel',
						style: 'cancel',
					},
					{
						text: 'Set to zero',
						onPress: () => {
							void handleZeroStocktakeItem(item);
						},
					},
				],
			);
			return;
		}

		void handleZeroStocktakeItem(item);
	}, [handleZeroStocktakeItem]);

	const uncountedItemsCount = useMemo(() => {
		return items.filter(item => !valueExists(item.quantity)).length;
	}, [items]);

	const handleZeroUncountedItems = useCallback(async () => {
		try {
			await zeroUncountedItemsMutation({ variables: { id: stocktake.id } });
		}
		catch (error) {
			Toast.show('Unable to zero uncounted items');
			return;
		}

		Toast.show('Uncounted items set to zero');
		// The mutation response already refreshed the items in the cache; the refetch
		// only re-syncs derived header stats and must not surface as a zeroing error.
		await refetch().catch(() => {});
	}, [refetch, stocktake?.id, zeroUncountedItemsMutation]);

	const confirmZeroUncountedItems = useCallback(() => {
		if (!stocktake?.id || zeroingUncountedItems || !uncountedItemsCount) {
			return;
		}

		Alert.alert(
			'Zero uncounted items?',
			`This sets the counted quantity to 0 for ${formatUncountedProductsPhrase(uncountedItemsCount)} in this stocktake, including any hidden by the current filter. Counted products are not affected.`,
			[
				{
					text: 'Cancel',
					style: 'cancel',
				},
				{
					text: 'Zero uncounted',
					onPress: () => {
						void handleZeroUncountedItems();
					},
				},
			],
		);
	}, [handleZeroUncountedItems, stocktake?.id, uncountedItemsCount, zeroingUncountedItems]);

	const handleSubmitStocktake = useCallback(async () => {
		if (!stocktake?.id) {
			return;
		}

		try {
			await applyStocktakeChangesMutation({ variables: { id: stocktake.id } });
			Toast.show('Stocktake submitted');
			await refetch();
		}
		catch (error) {
			Toast.show('Unable to submit stocktake');
		}
	}, [applyStocktakeChangesMutation, refetch, stocktake?.id]);

	const confirmSubmitStocktake = useCallback(() => {
		if (!stocktake?.id || submittingStocktake) {
			return;
		}

		Alert.alert(
			'Submit stocktake?',
			'Are you sure you want to submit this stocktake? This updates warehouse stock levels.',
			[
				{
					text: 'Cancel',
					style: 'cancel',
				},
				{
					text: 'Submit',
					onPress: () => {
						void handleSubmitStocktake();
					},
				},
			],
		);
	}, [handleSubmitStocktake, stocktake?.id, submittingStocktake]);

	const renderItem = useCallback(({ item }) => {
		const displayQuantity = valueExists(item.quantity) ? item.quantity : null;
		return (
			<StocktakeItemCard
				item={item}
				quantity={displayQuantity}
				onEdit={canEditStocktakes ? () => openBatchDialog(item) : undefined}
				onZero={canEditStocktakes ? () => confirmZeroStocktakeItem(item) : undefined}
				canEdit={canEditStocktakes}
			/>
		);
	}, [canEditStocktakes, confirmZeroStocktakeItem, openBatchDialog]);

	const keyExtractor = useCallback(item => item.id, []);

	const listHeaderComponent = (
		<StocktakeDetailHeader
			stocktake={stocktake}
			isStocktakeCompleted={isStocktakeCompleted}
			canEditStocktakes={canEditStocktakes}
			onOpenScanner={() => setIsScannerOpen(true)}
			onOpenManualSelect={() => setIsManualSelectOpen(true)}
			onZeroUncounted={confirmZeroUncountedItems}
			zeroingUncountedItems={zeroingUncountedItems}
			uncountedItemsCount={uncountedItemsCount}
			searchQuery={searchQuery}
			onSearchChange={handleSearchChange}
			showScannedOnly={showScannedOnly}
			onShowScannedOnlyChange={setShowScannedOnly}
		/>
	);

	useReportDockAction(onDockChange, {
		isVisible: Boolean(stocktake),
		label: 'Submit stocktake',
		onPress: confirmSubmitStocktake,
		loading: submittingStocktake,
		disabled: !canEditStocktakes,
	});

	if (!stocktake) {
		return (
			<View style={styles.container}>
				<Spinner />
			</View>
		);
	}

	return (
		<View style={styles.container}>
			<FlatList
				data={filteredItems}
				keyExtractor={keyExtractor}
				renderItem={renderItem}
				extraData={items}
				contentContainerStyle={styles.listContent}
				ListHeaderComponent={listHeaderComponent}
				style={styles.list}
				ListEmptyComponent={(
					<View style={styles.emptyState}>
						<EmptyState icon="search" title="No products match this filter" />
						{canEditStocktakes && (
							<View style={styles.buttonRow}>
								<SecondaryButton
									style={styles.halfButton}
									icon="camera"
									label="Scan product"
									onPress={() => setIsScannerOpen(true)}
								/>
								<SecondaryButton
									style={styles.halfButton}
									icon="search"
									label="Select product"
									onPress={() => setIsManualSelectOpen(true)}
								/>
							</View>
						)}
					</View>
				)}
				refreshControl={(
					<RefreshControl
						refreshing={isRefreshing}
						onRefresh={refreshPage}
					/>
				)}
			/>
			<StocktakeScannerDialog
				isOpen={isScannerOpen}
				onClose={() => setIsScannerOpen(false)}
				onBarcodeScanned={handleScanResult}
			/>
			<StocktakeBatchDialog
				item={resolvedActiveItem}
				barcode={activeBatchItem?.barcode}
				rows={batchDialogRows}
				setRows={setBatchDialogRows}
				currentWarehouseRows={batchDialogWarehouseRows}
				error={batchDialogError}
				onClose={closeBatchDialog}
				onSubmit={handleSaveBatches}
				loading={updatingItem}
				loadingWarehouseRows={isFetchingWarehouseBatches}
				canEdit={canEditStocktakes}
				timezone={
					resolvedActiveItem?.warehouseProduct?.tenants?.[0]?.timezone ||
					resolvedActiveItem?.warehouseProduct?.rootTenant?.timezone ||
					tenant?.timezone
				}
			/>
			<ManualProductSelectorDialog
				isOpen={isManualSelectOpen}
				onClose={() => setIsManualSelectOpen(false)}
				onSelect={handleManualSelectionRequest}
				items={items}
				canEdit={canEditStocktakes}
			/>
		</View>
	);
};

export default DetailContainer(StocktakeDetail);
